/* @jsx h */
import {
  defineComponent,
  ref,
  h,
  computed,
  onMounted,
  onBeforeUnmount,
  nextTick,
  Teleport,
} from "vue";
import { useLocalization } from "../Localization";
import { LemmonModal } from "../Modal";
import dayjs from "dayjs";
import { animate, spring } from "animejs";
import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/vue";
import { DateRangePanel, RangeFooter } from "./DateRangePanel";
import type { RangeEnd } from "./RangeFields";
import {
  DAY_END,
  DAY_START,
  isWholeDayRange,
  type DateRange,
  type DateRangeOption,
  type TimeOfDay,
} from "./types";
import {
  getDateRange,
  DATE_RANGE_PRESET_VALUES,
} from "@lemmonhq/blogic/date-time";

import type { DateRangeType } from "@lemmonhq/types";
export type { DateRangeType };
export type { DateRange, DateRangeOption, TimeOfDay };
export { getDateRange, DAY_START, DAY_END, isWholeDayRange };
export { syncedRef, resetSyncedRefs } from "./syncedRef";
export type { SyncedTimeWindow } from "./syncedRef";

/**
 * Matches Tailwind's `sm` breakpoint. The panel's own responsive classes are
 * `sm:`-prefixed, so any other value here would render the mobile layout
 * inside the desktop dropdown (or vice versa) in the gap between the two.
 */
const MOBILE_BREAKPOINT = 640;

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  className?: string;
  disabled?: boolean;
  options?: DateRangeOption[];
  /** Restrict selection to the supplied presets and hide custom calendars. */
  presetsOnly?: boolean;
  /**
   * Show `from`/`to` time inputs and carry `startTime`/`endTime` on the
   * emitted range. Off by default: most reports are day-granularity, and a
   * caller that does not read the time fields should not show controls that
   * silently do nothing.
   */
  showTime?: boolean;
}

export const DateRangePicker = defineComponent<DateRangePickerProps>({
  name: "DateRangePicker",
  props: [
    "value",
    "onChange",
    "className",
    "options",
    "presetsOnly",
    "disabled",
    "showTime",
  ],
  setup(props) {
    const { t, locale } = useLocalization();

    // ── open / close ──────────────────────────────────────────────────────────
    const isOpen = ref(false);
    const isMobile = ref(false);
    const wrapperRef = ref<HTMLElement | null>(null);
    const referenceEl = ref<HTMLElement | null>(null);
    const dropdownRef = ref<HTMLElement | null>(null);

    const { floatingStyles, update } = useFloating(referenceEl, dropdownRef, {
      placement: "bottom-start",
      whileElementsMounted: autoUpdate,
      middleware: [offset(8), flip(), shift({ padding: 8 })],
    });

    // ── draft ─────────────────────────────────────────────────────────────────
    // Time inputs fire on every keystroke, so edits accumulate in a draft and
    // only reach `onChange` on Apply. Presets are the exception: they are an
    // unambiguous single tap and commit immediately, as they always have.
    const withDefaults = (range: DateRange): Required<DateRange> => ({
      ...range,
      startTime: range.startTime ?? DAY_START,
      endTime: range.endTime ?? DAY_END,
    });

    const draft = ref<Required<DateRange>>(withDefaults(props.value));
    const activeEnd = ref<RangeEnd>("start");
    const leftMonth = ref(dayjs(props.value.startDate).startOf("month"));
    const rightMonth = computed(() => leftMonth.value.add(1, "month"));

    const isValid = computed(
      () =>
        dayjs(draft.value.startDate).isValid() &&
        dayjs(draft.value.endDate).isValid() &&
        draft.value.startDate <= draft.value.endDate,
    );

    // ── presets ───────────────────────────────────────────────────────────────
    const presetLabelKeys: Record<DateRangeType, string> = {
      today: "date_range_picker.options.today",
      yesterday: "date_range_picker.options.yesterday",
      this_week: "date_range_picker.options.this_week",
      last_week: "date_range_picker.options.last_week",
      this_month: "date_range_picker.options.this_month",
      last_month: "date_range_picker.options.last_month",
      this_quarter: "date_range_picker.options.this_quarter",
      last_quarter: "date_range_picker.options.last_quarter",
      this_year: "date_range_picker.options.this_year",
      last_year: "date_range_picker.options.last_year",
      last_12m: "date_range_picker.options.last_12_months",
      custom: "date_range_picker.options.custom_range",
    };
    const defaultOptions = computed<DateRangeOption[]>(() =>
      DATE_RANGE_PRESET_VALUES.map((value) => ({
        value,
        label: t(presetLabelKeys[value]),
      })),
    );
    const options = computed(() => props.options ?? defaultOptions.value);

    // ── button label ──────────────────────────────────────────────────────────
    const formatShort = (date: string) =>
      new Intl.DateTimeFormat(locale.value || "en", {
        day: "numeric",
        month: "short",
        year: "2-digit",
      }).format(dayjs(date).toDate());

    const buttonLabel = computed(() => {
      const { type, startDate, endDate } = props.value;
      const base =
        type === "custom"
          ? startDate === endDate
            ? formatShort(startDate)
            : `${formatShort(startDate)} – ${formatShort(endDate)}`
          : (options.value.find((o) => o.value === type)?.label ?? type);

      // Surface a non-default time window on the trigger, otherwise it is
      // invisible until the panel is reopened.
      if (!props.showTime || isWholeDayRange(props.value)) return base;
      return `${base} · ${props.value.startTime ?? DAY_START}–${props.value.endTime ?? DAY_END}`;
    });

    // ── open / close helpers ──────────────────────────────────────────────────
    const closeDropdown = () => {
      if (!isOpen.value) return;
      const el = dropdownRef.value;
      if (!el || isMobile.value) {
        isOpen.value = false;
        return;
      }
      animate(el, {
        opacity: [1, 0],
        translateY: [0, -8],
        duration: 150,
        ease: "easeInCubic",
        onComplete: () => {
          isOpen.value = false;
        },
      });
    };

    const openDropdown = async () => {
      if (isOpen.value) return;
      draft.value = withDefaults(props.value);
      activeEnd.value = "start";
      leftMonth.value = dayjs(props.value.startDate).startOf("month");
      isOpen.value = true;
      await nextTick();
      if (isMobile.value) return;
      update();
      const el = dropdownRef.value;
      if (!el) return;
      animate(el, {
        opacity: [0, 1],
        translateY: [-10, 0],
        ease: spring({ bounce: 0.25, duration: 500 }),
      });
    };

    const toggle = () => {
      if (props.disabled) return;
      if (isOpen.value) closeDropdown();
      else void openDropdown();
    };

    // ── interaction ───────────────────────────────────────────────────────────
    const handleDateClick = (date: string) => {
      const d = draft.value;
      if (activeEnd.value === "start") {
        draft.value = {
          ...d,
          startDate: date,
          endDate: date > d.endDate ? date : d.endDate,
          type: "custom",
        };
        activeEnd.value = "end";
      } else if (date < d.startDate) {
        // Tapping before the start restarts the range from there rather than
        // silently swapping the two ends under the user.
        draft.value = { ...d, startDate: date, endDate: date, type: "custom" };
        activeEnd.value = "end";
      } else {
        draft.value = { ...d, endDate: date, type: "custom" };
        activeEnd.value = "start";
      }
    };

    /**
     * Date-only callers get exactly the shape they got before time existed —
     * no `startTime`/`endTime` keys at all — so nothing downstream has to
     * learn about a field its query ignores.
     */
    const commit = (range: Required<DateRange>) => {
      props.onChange(
        props.showTime
          ? { ...range }
          : {
              startDate: range.startDate,
              endDate: range.endDate,
              type: range.type,
            },
      );
      closeDropdown();
    };

    const handlePresetClick = (type: DateRangeType) => {
      // A preset only answers "which days"; it says nothing about the time of
      // day. Resolve it to real dates, then carry the window already in the
      // panel — the caller's default (for the Data Room, the restaurant's
      // opening hours) or whatever the user typed into the time fields. Forcing
      // a whole day here silently discarded that window on every preset tap,
      // and left `00:00`/`23:59` pinned wherever the range is persisted.
      commit({
        ...getDateRange(type),
        type,
        startTime: draft.value.startTime,
        endTime: draft.value.endTime,
      });
    };

    const handleStartTimeChange = (value: TimeOfDay) => {
      // Time edits keep the preset type: "today, 09:00–17:00" stays a rolling
      // today rather than collapsing into a frozen custom range.
      draft.value = { ...draft.value, startTime: value };
    };
    const handleEndTimeChange = (value: TimeOfDay) => {
      draft.value = { ...draft.value, endTime: value };
    };

    const handleApply = () => {
      if (!isValid.value) return;
      commit(draft.value);
    };

    const stopInsideInteraction = (event: MouseEvent | TouchEvent) => {
      event.stopPropagation();
    };

    // ── outside click ─────────────────────────────────────────────────────────
    const handleOutsideClick = (e: MouseEvent | TouchEvent) => {
      if (isMobile.value) return;
      const target = e.target as Node;
      if (
        referenceEl.value?.contains(target) ||
        dropdownRef.value?.contains(target)
      ) {
        return;
      }
      closeDropdown();
    };

    const updateIsMobile = () => {
      const next = window.innerWidth < MOBILE_BREAKPOINT;
      // Crossing the breakpoint swaps sheet for dropdown mid-edit, which loses
      // the floating position and the open animation. Close instead.
      if (isOpen.value && next !== isMobile.value) isOpen.value = false;
      isMobile.value = next;
    };

    onMounted(() => {
      updateIsMobile();
      window.addEventListener("resize", updateIsMobile);
      document.addEventListener("mousedown", handleOutsideClick);
      document.addEventListener("touchstart", handleOutsideClick);
    });
    onBeforeUnmount(() => {
      window.removeEventListener("resize", updateIsMobile);
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    });

    const renderPanel = () => (
      <DateRangePanel
        options={options.value}
        selectedType={draft.value.type}
        startDate={draft.value.startDate}
        endDate={draft.value.endDate}
        startTime={draft.value.startTime}
        endTime={draft.value.endTime}
        activeEnd={activeEnd.value}
        showTime={!!props.showTime}
        leftMonth={leftMonth.value}
        rightMonth={rightMonth.value}
        isMobile={isMobile.value}
        presetsOnly={!!props.presetsOnly}
        onActivate={(end) => (activeEnd.value = end)}
        onPresetClick={handlePresetClick}
        onDateClick={handleDateClick}
        onStartTimeChange={handleStartTimeChange}
        onEndTimeChange={handleEndTimeChange}
        onLeftMonthChange={(m) => (leftMonth.value = m)}
        onRightMonthChange={(m) => (leftMonth.value = m.subtract(1, "month"))}
      />
    );

    const renderFooter = () => (
      <RangeFooter
        startDate={draft.value.startDate}
        endDate={draft.value.endDate}
        canApply={isValid.value}
        onCancel={closeDropdown}
        onApply={handleApply}
      />
    );

    // ── render ────────────────────────────────────────────────────────────────
    return () => (
      <div
        ref={wrapperRef}
        class={`relative inline-block ${props.className ?? ""}`}
      >
        <button
          ref={referenceEl}
          type="button"
          class={[
            "btn btn-secondary gap-2 flex-nowrap font-normal",
            props.disabled ? "opacity-50 cursor-not-allowed" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={toggle}
          disabled={props.disabled}
        >
          <span class="material-symbols-outlined shrink-0 text-lg">
            date_range
          </span>
          <span class="min-w-0 flex-1 truncate text-left text-sm">
            {buttonLabel.value}
          </span>
          <span
            class={[
              "material-symbols-outlined shrink-0 text-lg",
              "transition-transform duration-200",
              isOpen.value ? "rotate-180" : "",
            ].join(" ")}
          >
            keyboard_arrow_down
          </span>
        </button>

        {/* ── Mobile: bottom sheet, Apply pinned in the sticky slot ── */}
        {isOpen.value && isMobile.value && (
          <LemmonModal
            title={
              <span class="text-base font-normal">
                {t("date_range_picker.modal.title")}
              </span>
            }
            onClose={closeDropdown}
            renderButtons={props.presetsOnly ? undefined : renderFooter}
          >
            {renderPanel()}
          </LemmonModal>
        )}

        {/* ── Desktop: floating dropdown ── */}
        {isOpen.value && !isMobile.value && (
          <Teleport to="body">
            <div
              ref={dropdownRef}
              onMousedown={stopInsideInteraction}
              onTouchstart={stopInsideInteraction}
              onClick={stopInsideInteraction}
              class={[
                "z-[1200] overflow-hidden",
                "rounded-2xl border  bg-base-100 shadow-2xl",
                "w-auto max-w-[calc(100vw-1rem)]",
                "max-h-[calc(100vh-5rem)] overflow-y-auto",
              ].join(" ")}
              style={{ ...floatingStyles.value, transformOrigin: "top left" }}
            >
              {renderPanel()}
              {!props.presetsOnly && renderFooter()}
            </div>
          </Teleport>
        )}
      </div>
    );
  },
});
